import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import { runGraphqlQuery, useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import {
  usePaginatedQuery,
  type PaginatedFetchOptions,
  type RouteSort,
} from '~/hooks/usePaginatedQuery';
import type { OrganizationFilterInput } from '~/graphql/graphql';
import { CART_QUERY_KEYS } from '~/utils/cartCache';
import { SELLER_CONTACTS_PAGE_QUERY_KEY } from '~/api/sellerContacts';

const getOrganizationsQuery = graphql(`
  query GetOrganizations($page: Int!, $size: Int!, $name: StringOperatorInput) {
    organizations(
      pageInput: { page: $page, size: $size }
      filter: { name: $name }
    ) {
      content {
        id
        name
        sellingOrganizationCode
      }
      paging {
        totalElements
        totalPages
        number
        size
      }
    }
  }
`);

const getTradingPartnerOrganizationsForBuyerQuery = graphql(`
  query GetTradingPartnerOrganizationsForBuyer(
    $page: Int!
    $size: Int!
    $name: StringOperatorInput
    $excludeOrganizationIds: [ID!]
    $onlyOrganizationIds: [ID!]
  ) {
    tradingPartnerOrganizationsForBuyer(
      pageInput: { page: $page, size: $size }
      filter: { name: $name }
      excludeOrganizationIds: $excludeOrganizationIds
      onlyOrganizationIds: $onlyOrganizationIds
    ) {
      content {
        id
        name
        sellingOrganizationCode
        defaultCurrency
        sellerContactCount
        facilities {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
          }
          locations {
            id
            code
          }
        }
      }
      paging {
        totalElements
        totalPages
        number
        size
      }
    }
  }
`);

const addTradingPartnersToProductComparisonMutation = graphql(`
  mutation AddTradingPartnersToProductComparison(
    $input: AddTradingPartnersToProductInput!
  ) {
    addTradingPartnersToProductComparison(input: $input) {
      configurations {
        id
      }
      addedOrganizationIds
    }
  }
`);

const removeTradingPartnersFromProductComparisonMutation = graphql(`
  mutation RemoveTradingPartnersFromProductComparison(
    $input: RemoveTradingPartnersFromProductComparisonInput!
  ) {
    removeTradingPartnersFromProductComparison(input: $input)
  }
`);

const createOrganizationMutation = graphql(`
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
      name
    }
  }
`);

const getMySellersQuery = graphql(`
  query GetMySellers {
    mySellers {
      organizationId
      organizationName
      buyingOrganizationCode
      sellingOrganizationCode
    }
  }
`);

const getMyBuyersQuery = graphql(`
  query GetMyBuyers {
    myBuyers {
      organizationId
      organizationName
      buyingOrganizationCode
      sellingOrganizationCode
    }
  }
`);

const updateMySellerCodeMutation = graphql(`
  mutation UpdateMySellerCode($sellerOrganizationId: ID!, $code: String) {
    updateMySellerCode(
      sellerOrganizationId: $sellerOrganizationId
      code: $code
    ) {
      organizationId
      organizationName
      buyingOrganizationCode
      sellingOrganizationCode
    }
  }
`);

const updateMyBuyerCodeMutation = graphql(`
  mutation UpdateMyBuyerCode($buyerOrganizationId: ID!, $code: String) {
    updateMyBuyerCode(buyerOrganizationId: $buyerOrganizationId, code: $code) {
      organizationId
      organizationName
      buyingOrganizationCode
      sellingOrganizationCode
    }
  }
`);

const getBuyerMarginsQuery = graphql(`
  query GetBuyerMargins($platformProductLengthId: ID!) {
    buyerMargins(platformProductLengthId: $platformProductLengthId) {
      id
      marginRate
      pricePerUnit {
        amount
        currency
      }
      buyingOrganization {
        id
        name
      }
    }
  }
`);

const getBuyerNegotiatedPricesQuery = graphql(`
  query GetBuyerNegotiatedPrices($platformProductLengthId: ID!) {
    buyerNegotiatedPrices(platformProductLengthId: $platformProductLengthId) {
      buyingOrganization {
        id
        name
      }
      price {
        amount
        currency
      }
    }
  }
`);

const setBuyerMarginMutation = graphql(`
  mutation SetBuyerMargin(
    $platformProductLengthId: ID!
    $buyingOrganizationId: ID!
    $marginRate: Float!
  ) {
    setBuyerMargin(
      platformProductLengthId: $platformProductLengthId
      buyingOrganizationId: $buyingOrganizationId
      marginRate: $marginRate
    ) {
      id
      marginRate
      pricePerUnit {
        amount
        currency
      }
      buyingOrganization {
        id
        name
      }
    }
  }
`);

const removeBuyerMarginMutation = graphql(`
  mutation RemoveBuyerMargin(
    $platformProductLengthId: ID!
    $buyingOrganizationId: ID!
  ) {
    removeBuyerMargin(
      platformProductLengthId: $platformProductLengthId
      buyingOrganizationId: $buyingOrganizationId
    )
  }
`);

const getOrganizationQuery = graphql(`
  query GetOrganization($id: ID!) {
    organization(id: $id) {
      id
      name
      phoneNumber
      faxNumber
      tenantAdmins {
        id
        firstName
        lastName
        externalId
        phoneNumber
        status
        lastEditedAt
        lastEditedBy {
          id
          firstName
          lastName
        }
      }
    }
  }
`);

const getLinkedOrganizationQuery = graphql(`
  query GetLinkedOrganization($id: ID!) {
    organization(id: $id) {
      id
      name
      sellingOrganizationCode
      users {
        id
        firstName
        lastName
        externalId
        employeeId
        phoneNumber
        role
        status
        lastEditedAt
        lastEditedBy {
          id
          firstName
          lastName
        }
      }
    }
  }
`);

const getLinkedOrganizationUsersQuery = graphql(`
  query GetLinkedOrganizationUsers {
    linkedOrganizationUsers {
      id
      firstName
      lastName
      externalId
      phoneNumber
      role
      status
      lastEditedAt
      lastEditedBy {
        id
        firstName
        lastName
      }
      organization {
        id
        name
      }
    }
  }
`);

const createContactMutation = graphql(`
  mutation CreateContact($input: ContactInput!) {
    createContact(input: $input) {
      id
      firstName
      lastName
      phoneNumber
      externalId
    }
  }
`);

const updateContactMutation = graphql(`
  mutation UpdateContact($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      firstName
      lastName
    }
  }
`);

const createTenantAdminMutation = graphql(`
  mutation CreateTenantAdmin($input: CreateTenantAdminInput!) {
    createTenantAdmin(input: $input) {
      id
      firstName
      lastName
      externalId
      status
    }
  }
`);

const updateOrderNotificationEmailsMutation = graphql(`
  mutation UpdateOrderNotificationEmails(
    $input: OrderNotificationEmailsInput!
  ) {
    updateOrderNotificationEmails(input: $input) {
      id
      orderNotificationEmails {
        domestic
        international
      }
    }
  }
`);

const updateOrganizationContactMutation = graphql(`
  mutation UpdateOrganizationContact(
    $organizationId: ID!
    $input: OrganizationContactInput!
  ) {
    updateOrganizationContact(organizationId: $organizationId, input: $input) {
      id
      phoneNumber
      faxNumber
    }
  }
`);

const deactivateTenantAdminMutation = graphql(`
  mutation DeactivateTenantAdminForOrg($userId: ID!) {
    deactivateTenantUser(userId: $userId) {
      id
      status
    }
  }
`);

const reactivateTenantAdminMutation = graphql(`
  mutation ReactivateTenantAdminForOrg($userId: ID!) {
    reactivateTenantUser(userId: $userId) {
      id
      status
    }
  }
`);

const useGetOrganizations = (
  page: number,
  pageSize: number,
  filters?: OrganizationFilterInput,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  return useGraphqlQuery(
    getOrganizationsQuery,
    [
      'organizations',
      String(page),
      String(pageSize),
      JSON.stringify({
        name: filters?.name ?? null,
      }),
    ],
    {
      page,
      size: pageSize,
      name: filters?.name,
    },
    options
  );
};

export const useGetOrganizationsPaginated = (
  pageSize: number,
  search: string
) => {
  const nameFilter = search ? { contains: search } : undefined;
  const filters = nameFilter ? { name: nameFilter } : undefined;

  return usePaginatedQuery({
    fetchHook: useGetOrganizations,
    select: (data) => data?.organizations,
    options: {
      pageSize,
      filters,
    },
  });
};

export const useGetLinkedOrganization = (organizationId: string | undefined) =>
  useGraphqlQuery(
    getLinkedOrganizationQuery,
    ['linkedOrganization', organizationId ?? ''],
    { id: organizationId ?? '' },
    { enabled: !!organizationId }
  );

export const useFetchLinkedOrganizationUsers = () => {
  const queryClient = useQueryClient();
  return async () => {
    const data = await queryClient.fetchQuery({
      queryKey: ['linkedOrganizationUsers'],
      queryFn: ({ signal }) =>
        runGraphqlQuery(getLinkedOrganizationUsersQuery, undefined, signal),
    });
    return data.linkedOrganizationUsers.map(({ organization, ...user }) => {
      if (!organization) {
        throw new Error(
          `Linked organization user ${user.id} has no organization`
        );
      }
      return { ...user, organization };
    });
  };
};

const CONTACT_QUERY_KEYS = [
  'linkedOrganization',
  'tenantUsers',
  'organizationsByIds',
  'tradingPartnerOrganizationsForBuyer',
  SELLER_CONTACTS_PAGE_QUERY_KEY,
];

export const useCreateContact = () =>
  useGraphqlMutation(createContactMutation, CONTACT_QUERY_KEYS);

export const useUpdateContact = () =>
  useGraphqlMutation(updateContactMutation, CONTACT_QUERY_KEYS);

export const useCreateOrganization = () => {
  return useGraphqlMutation(createOrganizationMutation, ['organizations']);
};

type TradingPartnerOrganizationsForBuyerFilters = {
  search?: string;
  excludeOrganizationIds?: string[];
  onlyOrganizationIds?: string[];
};

export const useGetTradingPartnerOrganizationsForBuyer = (
  page: number,
  pageSize: number,
  filters?: TradingPartnerOrganizationsForBuyerFilters,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const nameFilter = filters?.search ? { contains: filters.search } : undefined;

  return useGraphqlQuery(
    getTradingPartnerOrganizationsForBuyerQuery,
    [
      'tradingPartnerOrganizationsForBuyer',
      page,
      pageSize,
      filters?.search ?? '',
      JSON.stringify(filters?.excludeOrganizationIds ?? []),
      JSON.stringify(filters?.onlyOrganizationIds ?? null),
    ],
    {
      page,
      size: pageSize,
      name: nameFilter,
      excludeOrganizationIds: filters?.excludeOrganizationIds,
      onlyOrganizationIds: filters?.onlyOrganizationIds,
    },
    { placeholderData: keepPreviousData, enabled: options?.enabled }
  );
};

export const useAddTradingPartnersToProductComparison = () =>
  useGraphqlMutation(addTradingPartnersToProductComparisonMutation, [
    'sellerProductInventories',
    'sellerVendorInventoryGroupsForBuyer',
    'cannedTallySellerGroupsForBuyer',
  ]);

export const useRemoveTradingPartnersFromProductComparison = () =>
  useGraphqlMutation(removeTradingPartnersFromProductComparisonMutation, [
    'sellerProductInventories',
    'sellerVendorInventoryGroupsForBuyer',
    'cannedTallySellerGroupsForBuyer',
  ]);

export const useGetOrganization = (organizationId: string | undefined) => {
  return useGraphqlQuery(
    getOrganizationQuery,
    ['organization', organizationId ?? ''],
    { id: organizationId! },
    { enabled: !!organizationId }
  );
};

// Invalidates both the paginated list and any open detail page
export const useCreateTenantAdmin = () =>
  useGraphqlMutation(createTenantAdminMutation, [
    'organizations',
    'organization',
  ]);

export const useDeactivateTenantAdminForOrg = () =>
  useGraphqlMutation(deactivateTenantAdminMutation, ['organization']);

export const useReactivateTenantAdminForOrg = () =>
  useGraphqlMutation(reactivateTenantAdminMutation, ['organization']);

export const useUpdateOrderNotificationEmails = () =>
  useGraphqlMutation(updateOrderNotificationEmailsMutation, ['cur']);

export const useUpdateOrganizationContact = () =>
  useGraphqlMutation(updateOrganizationContactMutation, ['organization']);

export const useGetMySellers = () => {
  return useGraphqlQuery(getMySellersQuery, ['mySellers']);
};

export const useGetMyBuyers = (enabled = true) => {
  return useGraphqlQuery(getMyBuyersQuery, ['myBuyers'], undefined, {
    enabled,
  });
};

// Every query that displays sellingOrganizationCode is invalidated so an edited code shows
// up immediately instead of waiting out its staleTime.
export const useUpdateMySellerCode = () =>
  useGraphqlMutation(updateMySellerCodeMutation, [
    'mySellers',
    'orders',
    ...CART_QUERY_KEYS,
    'sellerProductInventories',
    'requestForQuote',
    'myRequestForQuotes',
    'myRequestForQuoteFilterOptions',
    'buyLists',
    'buyerPriceNegotiations',
    'ordersWithChats',
    'shippingSoon',
    'sellerVendorInventoryGroupsForBuyer',
    'cannedTallySellerGroupsForBuyer',
    'tradingPartnerOrganizationsForBuyer',
  ]);

export const useUpdateMyBuyerCode = () =>
  useGraphqlMutation(updateMyBuyerCodeMutation, ['myBuyers']);

export const useGetBuyerMargins = (platformProductLengthId?: string) =>
  useGraphqlQuery(
    getBuyerMarginsQuery,
    ['buyerMargins', platformProductLengthId ?? ''],
    { platformProductLengthId: platformProductLengthId ?? '' },
    { enabled: !!platformProductLengthId }
  );

export const useGetBuyerNegotiatedPrices = (
  platformProductLengthId?: string,
  enabled = true
) =>
  useGraphqlQuery(
    getBuyerNegotiatedPricesQuery,
    ['buyerNegotiatedPrices', platformProductLengthId ?? ''],
    { platformProductLengthId: platformProductLengthId ?? '' },
    { enabled: enabled && !!platformProductLengthId }
  );

export const useSetBuyerMargin = () =>
  useGraphqlMutation(setBuyerMarginMutation, [
    'buyerMargins',
    'buyerNegotiatedPrices',
    'my-products',
  ]);

export const useRemoveBuyerMargin = () =>
  useGraphqlMutation(removeBuyerMarginMutation, [
    'buyerMargins',
    'my-products',
  ]);
